import React, { useEffect, useState } from 'react';
import { useScheduledNotifications } from '../hooks/useScheduledNotifications';
import '../styles/modal.scss';

const DAY_NAMES = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const emptyFilters = () => ({
    userFilters: {
        minTrips: '',
        maxTrips: '',
        minWarnings: '',
        maxWarnings: '',
        verified: 'any',
        startDate: '',
        endDate: '',
    },
    driverFilters: {
        state: 'any',
        vechicleType: 'any',
        minTrips: '',
        maxTrips: '',
        minWarnings: '',
        maxWarnings: '',
        verified: 'any',
        verificationStatus: 'any',
        startDate: '',
        endDate: '',
    },
});

const buildInitialFormData = (schedule) => {
    const base = {
        name: '',
        title: '',
        body: '',
        notificationType: 'for-users',
        repeatType: 'once',
        sendTime: '09:00',
        startDate: '',
        dayInterval: '1',
        dayOfWeek: '0',
        dayOfMonth: '1',
        ...emptyFilters(),
    };

    if (!schedule) return base;

    const startDate = schedule.startDate ? new Date(schedule.startDate) : null;
    const startDateStr = startDate && !Number.isNaN(startDate.getTime())
        ? startDate.toISOString().slice(0, 10)
        : '';

    return {
        ...base,
        name: schedule.name || '',
        title: schedule.title || '',
        body: schedule.body || '',
        notificationType: schedule.notificationType || 'for-users',
        repeatType: schedule.repeatType || 'once',
        sendTime: schedule.sendTime || '09:00',
        startDate: startDateStr,
        dayInterval: schedule.dayInterval ? String(schedule.dayInterval) : '1',
        dayOfWeek: schedule.dayOfWeek !== undefined && schedule.dayOfWeek !== null ? String(schedule.dayOfWeek) : '0',
        dayOfMonth: schedule.dayOfMonth ? String(schedule.dayOfMonth) : '1',
        userFilters: { ...base.userFilters, ...(schedule.userFilters || {}) },
        driverFilters: { ...base.driverFilters, ...(schedule.driverFilters || {}) },
    };
};

export const ScheduledNotificationModal = ({ isOpen, onClose, schedule = null }) => {
    const [formData, setFormData] = useState(() => buildInitialFormData(schedule));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const { createScheduledNotification, updateScheduledNotification } = useScheduledNotifications();

    const isEditing = Boolean(schedule);

    useEffect(() => {
        if (isOpen) {
            setFormData(buildInitialFormData(schedule));
            setError('');
        }
    }, [isOpen, schedule]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleNestedChange = (scope, field, value) => {
        setFormData(prev => ({
            ...prev,
            [scope]: { ...prev[scope], [field]: value },
        }));
        setError('');
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) return setError('اسم الجدولة مطلوب');
        if (!formData.title.trim()) return setError('عنوان الإشعار مطلوب');
        if (!formData.body.trim()) return setError('محتوى الإشعار مطلوب');
        if (!formData.sendTime) return setError('وقت الإرسال مطلوب');
        if (formData.repeatType === 'once' && !formData.startDate) return setError('تاريخ الإرسال مطلوب');
        if (formData.repeatType === 'daily' && (!formData.dayInterval || Number(formData.dayInterval) < 1)) {
            return setError('الفاصل بين الأيام يجب أن يكون رقمًا أكبر من صفر');
        }

        setIsLoading(true);
        setError('');

        try {
            const payload = {
                name: formData.name.trim(),
                title: formData.title.trim(),
                body: formData.body.trim(),
                notificationType: formData.notificationType,
                repeatType: formData.repeatType,
                sendTime: formData.sendTime,
            };

            if (formData.repeatType === 'once') {
                payload.startDate = formData.startDate;
            } else if (formData.repeatType === 'daily') {
                payload.startDate = formData.startDate || new Date().toISOString().slice(0, 10);
                payload.dayInterval = Number(formData.dayInterval);
            } else if (formData.repeatType === 'weekly') {
                payload.startDate = new Date().toISOString().slice(0, 10);
                payload.dayOfWeek = Number(formData.dayOfWeek);
            } else if (formData.repeatType === 'monthly') {
                payload.startDate = new Date().toISOString().slice(0, 10);
                payload.dayOfMonth = Number(formData.dayOfMonth);
            }

            if (formData.notificationType === 'for-users') {
                payload.userFilters = {
                    minTrips: formData.userFilters.minTrips,
                    maxTrips: formData.userFilters.maxTrips,
                    minWarnings: formData.userFilters.minWarnings,
                    maxWarnings: formData.userFilters.maxWarnings,
                    verified: formData.userFilters.verified === 'any' ? undefined : formData.userFilters.verified === 'true',
                    startDate: formData.userFilters.startDate || undefined,
                    endDate: formData.userFilters.endDate || undefined,
                };
            } else {
                payload.driverFilters = {
                    state: formData.driverFilters.state === 'any' ? undefined : formData.driverFilters.state,
                    vechicleType: formData.driverFilters.vechicleType === 'any' ? undefined : formData.driverFilters.vechicleType,
                    minTrips: formData.driverFilters.minTrips,
                    maxTrips: formData.driverFilters.maxTrips,
                    minWarnings: formData.driverFilters.minWarnings,
                    maxWarnings: formData.driverFilters.maxWarnings,
                    verified: formData.driverFilters.verified === 'any' ? undefined : formData.driverFilters.verified === 'true',
                    verificationStatus: formData.driverFilters.verificationStatus === 'any' ? undefined : formData.driverFilters.verificationStatus,
                    startDate: formData.driverFilters.startDate || undefined,
                    endDate: formData.driverFilters.endDate || undefined,
                };
            }

            if (isEditing) {
                await updateScheduledNotification(schedule._id, payload);
            } else {
                await createScheduledNotification(payload);
            }
            handleClose();
        } catch (err) {
            setError(err.message || 'حدث خطأ أثناء حفظ الجدولة');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content notification-modal scheduled-notification-modal" onClick={e => e.stopPropagation()}>
                <h2 className="TXT-heading2 color-normal">{isEditing ? 'تعديل الجدولة' : 'إنشاء جدولة جديدة'}</h2>

                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label>اسم الجدولة (للإدارة فقط)</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="مثال: تذكير نهاية الأسبوع"
                        />
                    </div>

                    <div className="input-group">
                        <label>إرسال إلى</label>
                        <select
                            value={formData.notificationType}
                            onChange={(e) => handleChange('notificationType', e.target.value)}
                        >
                            <option value="for-users">المستخدمين</option>
                            <option value="for-drivers">السائقين</option>
                        </select>
                    </div>

                    <div className="input-group">
                        <label>التكرار</label>
                        <select
                            value={formData.repeatType}
                            onChange={(e) => handleChange('repeatType', e.target.value)}
                        >
                            <option value="once">مرة واحدة</option>
                            <option value="daily">يوميًا</option>
                            <option value="weekly">أسبوعيًا</option>
                            <option value="monthly">شهريًا</option>
                        </select>
                    </div>

                    {formData.repeatType === 'once' && (
                        <div className="input-group">
                            <label>تاريخ الإرسال</label>
                            <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => handleChange('startDate', e.target.value)}
                                required
                            />
                        </div>
                    )}

                    {formData.repeatType === 'daily' && (
                        <div className="input-group">
                            <label>الفاصل بين الأيام</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.dayInterval}
                                onChange={(e) => handleChange('dayInterval', e.target.value)}
                            />
                            <p className="field-hint">1 = كل يوم، 2 = كل يومين، 3 = كل 3 أيام، وهكذا</p>
                        </div>
                    )}

                    {formData.repeatType === 'weekly' && (
                        <div className="input-group">
                            <label>يوم الأسبوع</label>
                            <select
                                value={formData.dayOfWeek}
                                onChange={(e) => handleChange('dayOfWeek', e.target.value)}
                            >
                                {DAY_NAMES.map((dayName, index) => (
                                    <option key={index} value={index}>{dayName}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formData.repeatType === 'monthly' && (
                        <div className="input-group">
                            <label>يوم الشهر</label>
                            <input
                                type="number"
                                min="1"
                                max="31"
                                value={formData.dayOfMonth}
                                onChange={(e) => handleChange('dayOfMonth', e.target.value)}
                            />
                            <p className="field-hint">إذا كان الشهر أقصر من اليوم المحدد سيتم الإرسال في آخر يوم منه</p>
                        </div>
                    )}

                    <div className="input-group">
                        <label>وقت الإرسال</label>
                        <input
                            type="time"
                            value={formData.sendTime}
                            onChange={(e) => handleChange('sendTime', e.target.value)}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label>عنوان الإشعار</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="أدخل عنوان الإشعار"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label>محتوى الإشعار</label>
                        <textarea
                            value={formData.body}
                            onChange={(e) => handleChange('body', e.target.value)}
                            placeholder="أدخل محتوى الإشعار"
                            rows="4"
                            required
                        />
                    </div>

                    {formData.notificationType === 'for-users' && (
                        <div className="filters-grid">
                            <div className="input-group">
                                <label>عدد الرحلات (من)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.userFilters.minTrips}
                                    onChange={(e) => handleNestedChange('userFilters', 'minTrips', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد الرحلات (إلى)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.userFilters.maxTrips}
                                    onChange={(e) => handleNestedChange('userFilters', 'maxTrips', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد التحذيرات (من)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.userFilters.minWarnings}
                                    onChange={(e) => handleNestedChange('userFilters', 'minWarnings', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد التحذيرات (إلى)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.userFilters.maxWarnings}
                                    onChange={(e) => handleNestedChange('userFilters', 'maxWarnings', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>حالة التحقق</label>
                                <select
                                    value={formData.userFilters.verified}
                                    onChange={(e) => handleNestedChange('userFilters', 'verified', e.target.value)}
                                >
                                    <option value="any">بدون تصفية</option>
                                    <option value="true">موثق</option>
                                    <option value="false">غير موثق</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>تاريخ الانضمام (من)</label>
                                <input
                                    type="date"
                                    value={formData.userFilters.startDate}
                                    onChange={(e) => handleNestedChange('userFilters', 'startDate', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>تاريخ الانضمام (إلى)</label>
                                <input
                                    type="date"
                                    value={formData.userFilters.endDate}
                                    onChange={(e) => handleNestedChange('userFilters', 'endDate', e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {formData.notificationType === 'for-drivers' && (
                        <div className="filters-grid">
                            <div className="input-group">
                                <label>الحالة</label>
                                <select
                                    value={formData.driverFilters.state}
                                    onChange={(e) => handleNestedChange('driverFilters', 'state', e.target.value)}
                                >
                                    <option value="any">بدون تصفية</option>
                                    <option value="active">نشط</option>
                                    <option value="inactive">غير نشط</option>
                                    <option value="deleted">محذوف</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>نوع المركبة</label>
                                <select
                                    value={formData.driverFilters.vechicleType}
                                    onChange={(e) => handleNestedChange('driverFilters', 'vechicleType', e.target.value)}
                                >
                                    <option value="any">بدون تصفية</option>
                                    <option value="ساحبة">ساحبة</option>
                                    <option value="رافعة">رافعة</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>عدد الرحلات (من)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.driverFilters.minTrips}
                                    onChange={(e) => handleNestedChange('driverFilters', 'minTrips', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد الرحلات (إلى)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.driverFilters.maxTrips}
                                    onChange={(e) => handleNestedChange('driverFilters', 'maxTrips', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد التحذيرات (من)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.driverFilters.minWarnings}
                                    onChange={(e) => handleNestedChange('driverFilters', 'minWarnings', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>عدد التحذيرات (إلى)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.driverFilters.maxWarnings}
                                    onChange={(e) => handleNestedChange('driverFilters', 'maxWarnings', e.target.value)}
                                    placeholder="اتركه فارغًا لتجاهل الشرط"
                                />
                            </div>
                            <div className="input-group">
                                <label>تم التحقق من الهاتف</label>
                                <select
                                    value={formData.driverFilters.verified}
                                    onChange={(e) => handleNestedChange('driverFilters', 'verified', e.target.value)}
                                >
                                    <option value="any">بدون تصفية</option>
                                    <option value="true">موثق</option>
                                    <option value="false">غير موثق</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>حالة التوثيق</label>
                                <select
                                    value={formData.driverFilters.verificationStatus}
                                    onChange={(e) => handleNestedChange('driverFilters', 'verificationStatus', e.target.value)}
                                >
                                    <option value="any">بدون تصفية</option>
                                    <option value="didnt-apply">لم يتقدم</option>
                                    <option value="pending">قيد المراجعة</option>
                                    <option value="rejected">مرفوض</option>
                                    <option value="approved">مقبول</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>تاريخ الانضمام (من)</label>
                                <input
                                    type="date"
                                    value={formData.driverFilters.startDate}
                                    onChange={(e) => handleNestedChange('driverFilters', 'startDate', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>تاريخ الانضمام (إلى)</label>
                                <input
                                    type="date"
                                    value={formData.driverFilters.endDate}
                                    onChange={(e) => handleNestedChange('driverFilters', 'endDate', e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    جاري الحفظ...
                                </>
                            ) : (isEditing ? 'حفظ التعديلات' : 'إنشاء الجدولة')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ScheduledNotificationModal;
